import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileText } from 'lucide-react';
import { useState } from 'react';
import { FormSection, MasterDataForm } from '../components/master-data/MasterDataForm';
import { FilterConfig, MasterDataTable, TableColumn } from '../components/master-data/MasterDataTable';
import { useAuth } from '../hooks/useAuth';
import { useToast } from '../hooks/useToast';

interface Company {
  _id: string;
  name: string;
  code: string;
  email?: string;
  contactNo?: string;
  faxNo?: string;
  website?: string;
  trackingUrl?: string;
  redirectUrl?: string;
  address?: string;
  city?: string;
  state?: string;
  country?: string;
  pincode?: string;
  defaultCustomer?: string | { _id: string; name: string };
  defaultVendor?: string | { _id: string; name: string };
  status: 'ACTIVE' | 'INACTIVE';
  panNumber?: string;
  cinNumber?: string;
  trnNumber?: string;
  taxType?: 'GST' | 'NON_GST';
  gstVatNo?: string;
  sacCode?: string;
  placeOfSupply?: string;
  addressLine1?: string;
  courierRegistrationNumber?: string;
  nameOfAuthorizedCourier?: string;
  whatsappToken?: string;
  whatsappUrlNumber?: string;
  awbNoPrefix?: string;
  msmeNumber?: string;
  textColor?: string;
  borderColor?: string;
  backgroundColor?: string;
  logo?: string;
  digitalSignature?: string;
  stamp?: string;
  authorizationLetter?: string;
  bankDetails?: {
    serialNo?: number;
    bankName?: string;
    bankBranch?: string;
    accountNumber?: string;
    accountType?: string;
    accountName?: string;
    bankSwiftId?: string;
    ifscCode?: string;
    bankAddress?: string;
    sortCode?: string;
    iban?: string;
    upiImage?: string;
    upiId?: string;
    upiNumber?: string;
    openingBlncType?: 'DEBIT' | 'CREDIT';
    openingBlncAmount?: number;
    openingBlncDate?: string;
    status?: 'ACTIVE' | 'INACTIVE';
    showQr?: 'YES' | 'NO';
  }[];
  invoiceRanges?: {
    nonGstInvoiceRangeCode?: string;
    prefix?: string;
    range?: string;
    startNumber?: number;
    endNumber?: number;
    serviceType?: string;
    rangeMasterHead?: string;
  }[];
  billingConfig?: {
    createAutoInvoice?: boolean;
    agingLimit?: string;
    billingCycle?: string;
    isGstApplicable?: boolean;
    isFscApplicable?: boolean;
    customBillingName?: string;
    contactPerson?: string;
    gstNo?: string;
    customBillingAddress?: string;
    pincode?: string;
    city?: string;
    state?: string;
    country?: string;
    contactNo?: string;
    effectiveFrom?: string;
    effectiveTill?: string;
  };
  createdAt: string;
  updatedAt: string;
}

const CompanyManagement = () => {
  const queryClient = useQueryClient();
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingCompany, setEditingCompany] = useState<Company | null>(null);
  const [serverErrors, setServerErrors] = useState<Record<string, string>>({});
  const { axios } = useAuth();
  const { toast } = useToast();

  // State for server-side filtering and pagination
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const [searchTerm, setSearchTerm] = useState('');
  const [currentFilters, setCurrentFilters] = useState<Record<string, string>>({});
  const [sortConfig, setSortConfig] = useState<{ sortBy?: string; sortOrder?: 'asc' | 'desc' }>({});

  // Fetch companies with server-side filtering and pagination
  const { data: companiesResponse, isLoading } = useQuery({
    queryKey: ['companies', currentPage, pageSize, searchTerm, currentFilters, sortConfig],
    queryFn: async () => {
      const params = new URLSearchParams();
      params.append('page', currentPage.toString());
      params.append('limit', pageSize.toString());
      
      if (searchTerm) {
        params.append('search', searchTerm);
      }
      
      // Add filters
      Object.entries(currentFilters).forEach(([key, value]) => {
        if (value && value !== 'all') {
          params.append(key, value);
        }
      });
      
      // Add sorting
      if (sortConfig.sortBy) {
        params.append('sortBy', sortConfig.sortBy);
        params.append('sortOrder', sortConfig.sortOrder || 'asc');
      }

      const response = await axios.get(`/api/master-data/company?${params.toString()}`);
      const result = response.data.data || {};
      console.log('Company API response:', result);
      
      return {
        data: result.data || [],
        pagination: {
          total: result.total || 0,
          page: result.page || 1,
          limit: result.limit || 10,
          totalPages: result.totalPages || 1
        }
      };
    },
  });

  const companies = companiesResponse?.data || [];
  const pagination = companiesResponse?.pagination;

  // Fetch customers for dropdown (for defaultCustomer field)
  const { data: customers = [] } = useQuery({
    queryKey: ['customers-dropdown'],
    queryFn: async () => {
      try {
        const response = await axios.get('/api/master-data/customer');
        return response.data?.data?.data || [];
      } catch (error) {
        console.error('Error fetching customers:', error);
        return [];
      }
    },
  });

  // Fetch vendors for dropdown (for defaultVendor field)
  const { data: vendors = [] } = useQuery({
    queryKey: ['vendors-dropdown'],
    queryFn: async () => {
      try {
        const response = await axios.get('/api/master-data/vendor');
        return response.data?.data?.data || [];
      } catch (error) {
        console.error('Error fetching vendors:', error);
        return [];
      }
    },
  });

  // Form submission state
  const [isSubmitting, setIsSubmitting] = useState(false);

  // Download company file function
  const downloadCompanyFile = async (company: Company, fileType: 'logo' | 'digitalSignature' | 'stamp' | 'authorizationLetter') => {
    try {
      const fileUrl = company[fileType];
      if (!fileUrl) {
        toast({
          title: 'Error',
          description: `${fileType} file not found for this company`,
          variant: 'destructive',
        });
        return;
      }

      const response = await axios.get(`/api/master-data/company/${company._id}/download/${fileType}`, {
        responseType: 'blob',
      });

      // Get filename from response headers
      const contentDisposition = response.headers['content-disposition'];
      const contentType = response.headers['content-type'];
      let filename = `${company.name.replace(/[^a-zA-Z0-9\-_]/g, '_')}_${fileType}`;
      
      if (contentDisposition) {
        // Try different patterns for filename extraction
        const patterns = [
          /filename\*=UTF-8''([^;]+)/,  // RFC 5987 format
          /filename="([^"]+)"/,         // Standard format with quotes
          /filename=([^;]+)/            // Simple format without quotes
        ];
        
        for (const pattern of patterns) {
          const match = contentDisposition.match(pattern);
          if (match) {
            let extractedFilename = match[1].trim();
            // Remove any trailing quotes
            extractedFilename = extractedFilename.replace(/^"|"$/g, '');
            filename = decodeURIComponent(extractedFilename);
            break;
          }
        }
      }
      
      // Create blob with proper content type
      const blob = new Blob([response.data], { type: contentType || 'application/octet-stream' });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement('a');
      link.href = url;
      link.setAttribute('download', filename);
      
      // Force download by adding to DOM and clicking
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);

      toast({
        title: 'Success',
        description: `${fileType} downloaded successfully`,
      });
    } catch (error: any) {
      console.error('Download error:', error);
      toast({
        title: 'Error',
        description: error.response?.data?.error?.message || `Failed to download ${fileType}`,
        variant: 'destructive',
      });
    }
  };

  // Delete company mutation
  const deleteCompanyMutation = useMutation({
    mutationFn: async (companyId: string) => {
      await axios.delete(`/api/master-data/company/${companyId}`);
    },
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['companies'] });
      toast({
        title: 'Company deleted',
        description: 'Company has been deleted successfully.',
      });
    },
    onError: (error: any) => {
      toast({
        title: 'Error',
        description: error.response?.data?.error?.message || 'Failed to delete company',
        variant: 'destructive',
      });
    },
  });

  const columns: TableColumn<Company>[] = [
    {
      key: 'code',
      label: 'Code',
      sortable: true,
      width: 'w-28',
    },
    {
      key: 'name',
      label: 'Company Name',
      sortable: true,
    },
    {
      key: 'email',
      label: 'Email',
      sortable: true,
      hideOnTablet: true,
    },
    {
      key: 'contactNo',
      label: 'Contact',
      sortable: true,
      hideOnTablet: true,
    },
    {
      key: 'awbNoPrefix',
      label: 'AWB Prefix',
      sortable: true,
      hideOnMobile: true,
    },
    {
      key: 'taxType',
      label: 'Tax Type',
      sortable: true,
      hideOnMobile: true,
      render: (value) => (
        <Badge variant={value === 'GST' ? 'default' : 'secondary'}>
          {value}
        </Badge>
      ),
    },
    {
      key: 'gstVatNo',
      label: 'GST/VAT No.',
      hideOnTablet: true,
    },
    {
      key: 'city',
      label: 'City',
      sortable: true,
      hideOnTablet: true,
    },
    {
      key: 'status',
      label: 'Status',
      render: (value) => (
        <Badge 
          variant={value === 'ACTIVE' ? 'default' : 'secondary'}
          className={value === 'ACTIVE' 
            ? 'bg-green-100 text-green-800 hover:bg-green-100' 
            : 'bg-red-100 text-red-800 hover:bg-red-100'
          }
        >
          {value === 'ACTIVE' ? 'Active' : 'Inactive'}
        </Badge>
      ),
    },
    {
      key: 'files',
      label: 'Files',
      hideOnMobile: true,
      render: (_, company) => (
        <div className="flex flex-wrap gap-1">
          {(['logo', 'digitalSignature', 'stamp', 'authorizationLetter'] as const).map((fileType) => {
            const fileUrl = company[fileType];
            if (!fileUrl) return null;

            const fileLabels = {
              logo: 'Logo',
              digitalSignature: 'Signature',
              stamp: 'Stamp',
              authorizationLetter: 'Auth Letter'
            };

            return (
              <Button
                key={fileType}
                variant="outline"
                size="sm"
                onClick={() => downloadCompanyFile(company, fileType)}
                className="h-7 px-2 text-xs"
                title={`Download ${fileLabels[fileType]}`}
              >
                <FileText className="h-3 w-3 mr-1" />
                {fileLabels[fileType]}
              </Button>
            );
          })}
        </div>
      ),
    },
  ];

  const filters: FilterConfig[] = [
    {
      key: 'status',
      label: 'Status',
      type: 'select',
      placeholder: 'Filter by status',
      options: [
        { value: 'all', label: 'All Statuses' },
        { value: 'ACTIVE', label: 'Active Companies' },
        { value: 'INACTIVE', label: 'Inactive Companies' },
      ],
    },
    {
      key: 'taxType',
      label: 'Tax Type',
      type: 'select',
      placeholder: 'Filter by tax type',
      options: [
        { value: 'all', label: 'All Tax Types' },
        { value: 'GST', label: 'GST Companies' },
        { value: 'NON_GST', label: 'Non-GST Companies' },
      ],
    },
    {
      key: 'city',
      label: 'City',
      type: 'text',
      placeholder: 'Search by city name...',
    },
    {
      key: 'state',
      label: 'State',
      type: 'text',
      placeholder: 'Search by state name...',
    },
  ];

  // Dynamic form sections based on mode (create vs edit)
  // Create dynamic form sections based on whether we're editing or creating
  const getFormSections = (): FormSection[] => [
    {
      title: 'Basic Information',
      description: 'Company basic details and contact information',
      fields: [
        {
          name: 'name',
          label: 'Company Name',
          type: 'text',
          required: true,
          placeholder: 'Enter unique company name',
        },
        {
          name: 'code',
          label: 'Company Code',
          type: 'text',
          placeholder: 'Auto-generated if left empty',
          description: 'Leave empty for auto-generation or enter custom code',
        },
        {
          name: 'email',
          label: 'Email Address',
          type: 'email',
          placeholder: 'Enter valid email address (optional)',
        },
        {
          name: 'contactNo',
          label: 'Contact Number',
          type: 'tel',
          placeholder: 'Enter phone number (optional)',
        },
        {
          name: 'faxNo',
          label: 'Fax Number',
          type: 'tel',
          placeholder: 'Enter fax number (optional)',
        },
        {
          name: 'website',
          label: 'Website',
          type: 'text',
          placeholder: 'Enter website URL (optional)',
        },
        {
          name: 'status',
          label: 'Status',
          type: 'radio',
          required: true,
          options: [
            { value: 'ACTIVE', label: 'Active' },
            { value: 'INACTIVE', label: 'Inactive' },
          ],
        },
      ],
    },
    {
      title: 'URLs & Configuration',
      description: 'Tracking and redirect URLs, default settings',
      fields: [
        {
          name: 'trackingUrl',
          label: 'Tracking URL',
          type: 'text',
          placeholder: 'Enter HTTPS tracking URL (optional)',
          description: 'Must be a valid HTTPS URL for tracking page',
        },
        {
          name: 'redirectUrl',
          label: 'Redirect URL',
          type: 'text',
          placeholder: 'Enter HTTPS redirect URL (optional)',
          description: 'Must be a valid HTTPS URL for tracking page redirect',
        },
        {
          name: 'defaultCustomer',
          label: 'Default Customer',
          type: 'select',
          options: customers.map((customer: any) => ({
            value: customer._id,
            label: `${customer.name} (${customer.code})`,
          })),
          placeholder: 'Select default customer (optional)',
        },
        {
          name: 'defaultVendor',
          label: 'Default Vendor',
          type: 'select',
          options: vendors.map((vendor: any) => ({
            value: vendor._id,
            label: `${vendor.name} (${vendor.code})`,
          })),
          placeholder: 'Select default vendor (optional)',
        },
      ],
    },
    {
      title: 'Address Information',
      description: 'Company address and location details',
      fields: [
        {
          name: 'address',
          label: 'Address',
          type: 'textarea',
          placeholder: 'Enter full physical address (optional)',
          rows: 3,
        },
        {
          name: 'city',
          label: 'City',
          type: 'text',
          placeholder: 'Enter city name (optional)',
        },
        {
          name: 'state',
          label: 'State',
          type: 'text',
          placeholder: 'Enter state name (optional)',
        },
        {
          name: 'country',
          label: 'Country',
          type: 'text',
          placeholder: 'Enter country name (optional)',
        },
        {
          name: 'pincode',
          label: 'Pincode',
          type: 'text',
          placeholder: 'Enter pincode (optional)',
        },
        {
          name: 'addressLine1',
          label: 'Registered Address',
          type: 'textarea',
          placeholder: 'Enter full registered address (optional)',
          rows: 2,
        },
        {
          name: 'placeOfSupply',
          label: 'Place of Supply',
          type: 'text',
          placeholder: 'Enter place of supply (optional)',
        },
      ],
    },
    {
      title: 'Tax & Legal Information',
      description: 'GST, PAN, CIN, TRN and other legal details',
      fields: [
        {
          name: 'panNumber',
          label: 'PAN Number',
          type: 'text',
          placeholder: 'Enter valid PAN number (e.g., ABCDE1234F) (optional)',
          description: 'Format: 5 letters, 4 digits, 1 letter',
        },
        {
          name: 'cinNumber',
          label: 'CIN Number',
          type: 'text',
          placeholder: 'Enter valid CIN number (optional)',
          description: 'Corporate Identity Number',
        },
        {
          name: 'trnNumber',
          label: 'TRN Number',
          type: 'text',
          placeholder: 'Enter valid TRN number (15 digits) (optional)',
          description: 'Tax Registration Number',
        },
        {
          name: 'taxType',
          label: 'Tax Type',
          type: 'radio',
          options: [
            { value: 'GST', label: 'GST' },
            { value: 'NON_GST', label: 'Non-GST' },
          ],
        },
        {
          name: 'gstVatNo',
          label: 'GST/VAT Number',
          type: 'text',
          placeholder: 'Enter valid GST/VAT number (optional)',
          description: 'Format: 15 character GST number',
        },
        {
          name: 'sacCode',
          label: 'SAC Code',
          type: 'text',
          placeholder: 'Enter valid SAC code (6 digits) (optional)',
          description: 'Service Accounting Code',
        },
        {
          name: 'placeOfSupply',
          label: 'Place of Supply',
          type: 'text',
          placeholder: 'Enter place of supply (optional)',
          description: 'Place of supply for tax purposes',
        },
      ],
    },
    {
      title: 'Courier Configuration',
      description: 'Courier-specific settings and configurations',
      fields: [
        {
          name: 'courierRegistrationNumber',
          label: 'Courier Registration Number',
          type: 'text',
          placeholder: 'Enter valid courier registration number (optional)',
        },
        {
          name: 'nameOfAuthorizedCourier',
          label: 'Name of Authorized Courier',
          type: 'text',
          placeholder: 'Enter name of authorized courier (optional)',
        },
        {
          name: 'awbNoPrefix',
          label: 'AWB No. Prefix',
          type: 'text',
          placeholder: 'Keep SR as prefix or enter custom prefix (optional)',
          description: 'Default prefix for AWB numbers',
        },
        {
          name: 'msmeNumber',
          label: 'MSME Number',
          type: 'text',
          placeholder: 'Enter valid MSME number (optional)',
          description: 'Micro, Small & Medium Enterprises number',
        },
      ],
    },
    {
      title: 'WhatsApp Configuration',
      description: 'WhatsApp integration settings',
      fields: [
        {
          name: 'whatsappToken',
          label: 'WhatsApp Token',
          type: 'text',
          placeholder: 'Enter WhatsApp token (optional)',
          description: 'Valid WhatsApp token number format',
        },
        {
          name: 'whatsappUrlNumber',
          label: 'WhatsApp URL',
          type: 'text',
          placeholder: 'Enter HTTPS WhatsApp URL (optional)',
          description: 'Must be a valid HTTPS link for WhatsApp',
        },
      ],
    },
    {
      title: 'Visual Settings',
      description: 'Color scheme and branding settings',
      fields: [
        {
          name: 'textColor',
          label: 'Text Color',
          type: 'text',
          placeholder: '#000000',
          description: 'HEX color code for text (optional)',
        },
        {
          name: 'borderColor',
          label: 'Border Color',
          type: 'text',
          placeholder: '#cccccc',
          description: 'HEX color code for borders (optional)',
        },
        {
          name: 'backgroundColor',
          label: 'Background Color',
          type: 'text',
          placeholder: '#ffffff',
          description: 'HEX color code for background (optional)',
        },
      ],
    },
    {
      title: 'Bank Details',
      description: 'Configure primary bank account information and UPI details (all optional)',
      fields: [
        {
          name: 'bankDetails.0.serialNo',
          label: 'Serial No',
          type: 'number',
          min: 1,
          placeholder: 'Enter serial number (starting from 1) (optional)',
          description: 'Serial number starting from 1',
        },
        {
          name: 'bankDetails.0.bankName',
          label: 'Bank Name',
          type: 'text',
          placeholder: 'Enter bank name (optional)',
          description: 'Name of the bank of the Company',
        },
        {
          name: 'bankDetails.0.bankBranch',
          label: 'Bank Branch',
          type: 'text',
          placeholder: 'Enter bank branch (optional)',
          description: 'Valid bank branch',
        },
        {
          name: 'bankDetails.0.accountNumber',
          label: 'Account Number',
          type: 'text',
          placeholder: 'Enter valid bank account number (optional)',
          description: 'Valid bank account number',
        },
        {
          name: 'bankDetails.0.accountType',
          label: 'Account Type',
          type: 'text',
          placeholder: 'Enter valid bank account type (e.g., Savings, Current) (optional)',
          description: 'Valid bank account type',
        },
        {
          name: 'bankDetails.0.accountName',
          label: 'Account Name',
          type: 'text',
          placeholder: 'Enter valid bank account name (optional)',
          description: 'Valid bank account name',
        },
        {
          name: 'bankDetails.0.bankSwiftId',
          label: 'Bank SWIFT ID',
          type: 'text',
          placeholder: 'Enter SWIFT ID (optional)',
          description: 'Valid bank SWIFT number',
        },
        {
          name: 'bankDetails.0.ifscCode',
          label: 'IFSC Code',
          type: 'text',
          placeholder: 'Enter IFSC code (optional)',
        },
        {
          name: 'bankDetails.0.bankAddress',
          label: 'Bank Address',
          type: 'textarea',
          placeholder: 'Enter bank address (optional)',
          rows: 2,
        },
        {
          name: 'bankDetails.0.sortCode',
          label: 'Sort Code',
          type: 'text',
          placeholder: 'Enter sort code (optional)',
          description: 'Valid IFSC code of bank format',
        },
        {
          name: 'bankDetails.0.iban',
          label: 'IBAN',
          type: 'text',
          placeholder: 'Enter IBAN number (optional)',
          description: 'Valid SORT code of bank format',
        },
        {
          name: 'bankDetails.0.upiImage',
          label: 'UPI Image',
          type: 'file',
          accept: '.jpg,.jpeg,.png,.pdf',
          placeholder: 'Upload UPI QR code image (optional)',
          description: 'Upload UPI image file of the Company',
          currentFileUrl: editingCompany?.bankDetails?.[0]?.upiImage ? 'file-exists' : undefined,
        },
        {
          name: 'bankDetails.0.upiId',
          label: 'UPI ID',
          type: 'text',
          placeholder: 'Enter UPI ID (optional)',
          description: 'Valid UPI id format',
        },
        {
          name: 'bankDetails.0.upiNumber',
          label: 'UPI Number',
          type: 'text',
          placeholder: 'Enter UPI number (optional)',
          description: 'Valid UPI number format',
        },
        {
          name: 'bankDetails.0.openingBlncType',
          label: 'Opening Balance Type',
          type: 'radio',
          options: [
            { value: 'DEBIT', label: 'Debit' },
            { value: 'CREDIT', label: 'Credit' },
          ],
          description: 'Select debit or credit (optional)',
        },
        {
          name: 'bankDetails.0.openingBlncAmount',
          label: 'Opening Balance Amount',
          type: 'number',
          min: 0,
          placeholder: 'Enter opening balance amount (optional)',
          description: 'Opening balance amount of the bank balance',
        },
        {
          name: 'bankDetails.0.openingBlncDate',
          label: 'Opening Balance Date',
          type: 'date',
          placeholder: 'Select opening balance date (optional)',
          description: 'Select the date of opening bank balance',
        },
        {
          name: 'bankDetails.0.status',
          label: 'Status',
          type: 'radio',
          options: [
            { value: 'ACTIVE', label: 'Active' },
            { value: 'INACTIVE', label: 'Inactive' },
          ],
          description: 'Select active or inactive (optional)',
        },
        {
          name: 'bankDetails.0.showQr',
          label: 'Show QR',
          type: 'radio',
          options: [
            { value: 'YES', label: 'Yes' },
            { value: 'NO', label: 'No' },
          ],
          description: 'Select yes or no (optional)',
        },
      ],
    },
    {
      title: 'Invoice Number Range',
      description: 'Configure primary invoice number range and prefixes (all optional)',
      fields: [
        {
          name: 'invoiceRanges.0.nonGstInvoiceRangeCode',
          label: 'Non-GST Invoice Range Code',
          type: 'select',
          options: [
            { value: 'RANGE001', label: 'Range 001' },
            { value: 'RANGE002', label: 'Range 002' },
            { value: 'RANGE003', label: 'Range 003' },
          ],
          placeholder: 'Select invoice range code (optional)',
        },
        {
          name: 'invoiceRanges.0.prefix',
          label: 'Prefix',
          type: 'text',
          placeholder: 'Enter prefix (optional)',
        },
        {
          name: 'invoiceRanges.0.range',
          label: 'Range',
          type: 'text',
          placeholder: 'Enter range (optional)',
        },
        {
          name: 'invoiceRanges.0.startNumber',
          label: 'Start Number',
          type: 'number',
          min: 1,
          placeholder: 'Enter start number (optional)',
        },
        {
          name: 'invoiceRanges.0.endNumber',
          label: 'End Number',
          type: 'number',
          min: 1,
          placeholder: 'Enter end number (optional)',
        },
        {
          name: 'invoiceRanges.0.serviceType',
          label: 'Service Type',
          type: 'select',
          options: [
            { value: 'EXPRESS', label: 'Express' },
            { value: 'STANDARD', label: 'Standard' },
            { value: 'ECONOMY', label: 'Economy' },
          ],
          placeholder: 'Select service type (optional)',
        },
        {
          name: 'invoiceRanges.0.rangeMasterHead',
          label: 'Range Master Head',
          type: 'select',
          options: [
            { value: 'HEAD001', label: 'Head 001' },
            { value: 'HEAD002', label: 'Head 002' },
            { value: 'HEAD003', label: 'Head 003' },
          ],
          placeholder: 'Select range master head (optional)',
        },
      ],
    },
    {
      title: 'Billing Configuration',
      description: 'Configure billing settings and preferences',
      fields: [
        {
          name: 'billingConfig.createAutoInvoice',
          label: 'Create Auto Invoice',
          type: 'checkbox',
          placeholder: 'Automatically create invoices',
        },
        {
          name: 'billingConfig.agingLimit',
          label: 'Aging Limit',
          type: 'text',
          placeholder: 'Enter aging limit (optional)',
        },
        {
          name: 'billingConfig.billingCycle',
          label: 'Billing Cycle',
          type: 'select',
          options: [
            { value: 'MONTHLY', label: 'Monthly' },
            { value: 'QUARTERLY', label: 'Quarterly' },
            { value: 'YEARLY', label: 'Yearly' },
          ],
          placeholder: 'Select billing cycle (optional)',
        },
        {
          name: 'billingConfig.isGstApplicable',
          label: 'Is GST Applicable',
          type: 'checkbox',
          placeholder: 'GST applicable for billing',
        },
        {
          name: 'billingConfig.isFscApplicable',
          label: 'Is FSC Applicable',
          type: 'checkbox',
          placeholder: 'FSC applicable for billing',
        },
        {
          name: 'billingConfig.customBillingName',
          label: 'Custom Billing Name',
          type: 'text',
          placeholder: 'Enter custom billing name (optional)',
        },
        {
          name: 'billingConfig.contactPerson',
          label: 'Contact Person',
          type: 'text',
          placeholder: 'Enter contact person name (optional)',
        },
        {
          name: 'billingConfig.gstNo',
          label: 'GST No.',
          type: 'text',
          placeholder: 'Enter GST number (optional)',
        },
        {
          name: 'billingConfig.customBillingAddress',
          label: 'Custom Billing Address',
          type: 'textarea',
          placeholder: 'Enter custom billing address (optional)',
          rows: 3,
        },
        {
          name: 'billingConfig.pincode',
          label: 'Pincode',
          type: 'text',
          placeholder: 'Enter 6-digit pincode (optional)',
        },
        {
          name: 'billingConfig.city',
          label: 'City',
          type: 'text',
          placeholder: 'Enter city name (optional)',
        },
        {
          name: 'billingConfig.state',
          label: 'State',
          type: 'text',
          placeholder: 'Enter state name (optional)',
        },
        {
          name: 'billingConfig.country',
          label: 'Country',
          type: 'text',
          placeholder: 'Enter country name (optional)',
        },
        {
          name: 'billingConfig.contactNo',
          label: 'Contact No.',
          type: 'tel',
          placeholder: 'Enter contact number (optional)',
        },
        {
          name: 'billingConfig.effectiveFrom',
          label: 'Effective From',
          type: 'date',
          placeholder: 'Select effective from date (optional)',
        },
        {
          name: 'billingConfig.effectiveTill',
          label: 'Effective Till',
          type: 'date',
          placeholder: 'Select effective till date (optional)',
        },
      ],
    },
    {
      title: 'File Uploads',
      description: 'Upload company documents and branding files (all optional)',
      fields: [
        {
          name: 'logo',
          label: 'Logo',
          type: 'file',
          required: false, // Optional for all companies
          accept: '.jpg,.jpeg,.png,.pdf',
          placeholder: 'Upload logo file (optional - JPG, PNG, PDF)',
          currentFileUrl: editingCompany?.logo ? 'file-exists' : undefined, // Show current file exists
          onDownloadFile: editingCompany?.logo ? () => downloadCompanyFile(editingCompany, 'logo') : undefined,
        },
        {
          name: 'digitalSignature',
          label: 'Digital Signature',
          type: 'file',
          required: false, // Optional for all companies
          accept: '.jpg,.jpeg,.png,.pdf',
          placeholder: 'Upload digital signature file (optional - JPG, PNG, PDF)',
          currentFileUrl: editingCompany?.digitalSignature ? 'file-exists' : undefined, // Show current file exists
          onDownloadFile: editingCompany?.digitalSignature ? () => downloadCompanyFile(editingCompany, 'digitalSignature') : undefined,
        },
        {
          name: 'stamp',
          label: 'Stamp',
          type: 'file',
          required: false, // Optional for all companies
          accept: '.jpg,.jpeg,.png,.pdf',
          placeholder: 'Upload stamp file (optional - JPG, PNG, PDF)',
          currentFileUrl: editingCompany?.stamp ? 'file-exists' : undefined, // Show current file exists
          onDownloadFile: editingCompany?.stamp ? () => downloadCompanyFile(editingCompany, 'stamp') : undefined,
        },
        {
          name: 'authorizationLetter',
          label: 'Authorization Letter',
          type: 'file',
          required: false, // Optional for all companies
          accept: '.jpg,.jpeg,.png,.pdf',
          placeholder: 'Upload authorization letter (optional - JPG, PNG, PDF)',
          currentFileUrl: editingCompany?.authorizationLetter ? 'file-exists' : undefined, // Show current file exists
          onDownloadFile: editingCompany?.authorizationLetter ? () => downloadCompanyFile(editingCompany, 'authorizationLetter') : undefined,
        },
      ],
    },
  ];

  const formSections = getFormSections();

  const handleAdd = () => {
    setEditingCompany(null);
    setServerErrors({});
    setIsFormOpen(true);
  };

  const handleEdit = (company: Company) => {
    // Flatten nested data for form fields
    const flattenedData: Record<string, any> = { ...company } as any;
    
    // Flatten bank details
    if (company.bankDetails && company.bankDetails.length > 0) {
      const bankDetail = company.bankDetails[0] as Record<string, any>;
      Object.keys(bankDetail).forEach(key => {
        if (key !== '_id') {
          // Convert serialNo to number explicitly to ensure proper type
          if (key === 'serialNo' && typeof bankDetail[key] === 'string') {
            (flattenedData as any)[`bankDetails.0.${key}`] = Number(bankDetail[key]);
          } else {
            (flattenedData as any)[`bankDetails.0.${key}`] = bankDetail[key];
          }
        }
      });
    }
    
    // Flatten invoice ranges
    if (company.invoiceRanges && company.invoiceRanges.length > 0) {
      const invoiceRange = company.invoiceRanges[0] as Record<string, any>;
      Object.keys(invoiceRange).forEach(key => {
        if (key !== '_id') {
          (flattenedData as any)[`invoiceRanges.0.${key}`] = invoiceRange[key];
        }
      });
    }
    
    // Flatten billing config
    if (company.billingConfig) {
      const billingConfig = company.billingConfig as Record<string, any>;
      Object.keys(billingConfig).forEach(key => {
        if (key !== '_id') {
          (flattenedData as any)[`billingConfig.${key}`] = billingConfig[key];
        }
      });
    }
    
    setEditingCompany({ ...company, ...flattenedData } as Company);
    setServerErrors({});
    setIsFormOpen(true);
  };

  const handleDelete = (company: Company) => {
    if (window.confirm(`Are you sure you want to delete company "${company.name}"?`)) {
      deleteCompanyMutation.mutate(company._id);
    }
  };

  const handleRefresh = () => {
    queryClient.invalidateQueries({ queryKey: ['companies'] });
  };

  // Export functionality
  const handleExport = async () => {
    try {
      const params = new URLSearchParams();
      
      // Add current filters to export (but not pagination - we want all records)
      if (searchTerm) {
        params.append('search', searchTerm);
      }
      
      Object.entries(currentFilters).forEach(([key, value]) => {
        if (value && value !== 'all') {
          params.append(key, value);
        }
      });

      const response = await axios.get(`/api/master-data/company/export?${params.toString()}`);
      
      if (response.data.success) {
        const data = response.data.data;
        
        // Convert to CSV
        if (data.length > 0) {
          const headers = ['Name', 'Code', 'Email', 'Contact', 'AWB Prefix', 'Tax Type', 'GST/VAT No.', 'PAN Number', 'City', 'State', 'Status', 'Created Date'];
          const csvContent = [
            headers.join(','),
            ...data.map((company: any) => [
              `"${company.name || ''}"`,
              `"${company.code || ''}"`,
              `"${company.email || ''}"`,
              `"${company.contactNo || ''}"`,
              `"${company.awbNoPrefix || ''}"`,
              `"${company.taxType || ''}"`,
              `"${company.gstVatNo || ''}"`,
              `"${company.panNumber || ''}"`,
              `"${company.city || ''}"`,
              `"${company.state || ''}"`,
              `"${company.status || ''}"`,
              `"${new Date(company.createdAt).toLocaleDateString()}"`
            ].join(','))
          ].join('\n');

          // Download CSV
          const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
          const link = document.createElement('a');
          link.href = URL.createObjectURL(blob);
          link.download = `companies_export_${new Date().toISOString().split('T')[0]}.csv`;
          link.click();
          
          toast({
            title: 'Export successful',
            description: `Exported ${data.length} company records.`,
          });
        } else {
          toast({
            title: 'No data to export',
            description: 'No companies match the current filters.',
            variant: 'destructive',
          });
        }
      }
    } catch (error: any) {
      console.error('Export error:', error);
      toast({
        title: 'Export failed',
        description: error.response?.data?.error || 'Failed to export companies',
        variant: 'destructive',
      });
    }
  };

  const handleFormSubmit = async (data: any) => {
    setIsSubmitting(true);
    try {
      // Separate files from other data
      const { logo, digitalSignature, stamp, authorizationLetter, ...companyData } = data;
      
      // Transform nested field names to actual nested objects
      const transformedData = { ...companyData };
      
      // Handle bankDetails nested fields
      const bankDetails: any = {};
      const invoiceRanges: any = {};
      const billingConfig: any = {};
      
      Object.keys(companyData).forEach(key => {
        if (key.startsWith('bankDetails.0.')) {
          const fieldName = key.replace('bankDetails.0.', '');
          if (companyData[key] !== undefined && companyData[key] !== '') {
            bankDetails[fieldName] = companyData[key];
          }
          delete transformedData[key];
        } else if (key.startsWith('invoiceRanges.0.')) {
          const fieldName = key.replace('invoiceRanges.0.', '');
          if (companyData[key] !== undefined && companyData[key] !== '') {
            invoiceRanges[fieldName] = companyData[key];
          }
          delete transformedData[key];
        } else if (key.startsWith('billingConfig.')) {
          const fieldName = key.replace('billingConfig.', '');
          if (companyData[key] !== undefined && companyData[key] !== '') {
            billingConfig[fieldName] = companyData[key];
          }
          delete transformedData[key];
        }
      });
      
      // Add structured nested data
      if (Object.keys(bankDetails).length > 0) {
        // Add serial number if not provided
        if (!bankDetails.serialNo) {
          bankDetails.serialNo = 1;
        }
        transformedData.bankDetails = [bankDetails];
      } else if (editingCompany && editingCompany.bankDetails) {
        // Preserve existing bank details when editing
        transformedData.bankDetails = editingCompany.bankDetails;
      }
      
      if (Object.keys(invoiceRanges).length > 0) {
        transformedData.invoiceRanges = [invoiceRanges];
      } else if (editingCompany && editingCompany.invoiceRanges) {
        // Preserve existing invoice ranges when editing
        transformedData.invoiceRanges = editingCompany.invoiceRanges;
      }
      
      if (Object.keys(billingConfig).length > 0) {
        transformedData.billingConfig = billingConfig;
      } else if (editingCompany && editingCompany.billingConfig) {
        // Preserve existing billing config when editing
        transformedData.billingConfig = editingCompany.billingConfig;
      }

      // Clean up undefined values
      Object.keys(transformedData).forEach(key => {
        if (transformedData[key] === undefined) {
          delete transformedData[key];
        }
      });

      // Debug: Log the transformed data
      console.log('Transformed data being sent:', JSON.stringify(transformedData, null, 2));

      // Create FormData for file upload
      const formData = new FormData();
      formData.append('companyData', JSON.stringify(transformedData));

      // Add files to FormData
      if (logo instanceof File) {
        formData.append('logo', logo);
      }
      if (digitalSignature instanceof File) {
        formData.append('digitalSignature', digitalSignature);
      }
      if (stamp instanceof File) {
        formData.append('stamp', stamp);
      }
      if (authorizationLetter instanceof File) {
        formData.append('authorizationLetter', authorizationLetter);
      }

      // Use the appropriate endpoint
      if (editingCompany) {
        const response = await axios.put(`/api/master-data/company/${editingCompany._id}/with-files`, formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        
        if (response.data.success) {
          queryClient.invalidateQueries({ queryKey: ['companies'] });
          toast({
            title: 'Company updated',
            description: 'Company has been updated successfully.',
          });
          setIsFormOpen(false);
          setEditingCompany(null);
          setServerErrors({});
        }
      } else {
        const response = await axios.post('/api/master-data/company/with-files', formData, {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        });
        
        if (response.data.success) {
          queryClient.invalidateQueries({ queryKey: ['companies'] });
          toast({
            title: 'Company created',
            description: 'Company has been created successfully.',
          });
          setIsFormOpen(false);
          setEditingCompany(null);
          setServerErrors({});
        }
      }
    } catch (error: any) {
      console.error('Form submission error:', error);
      
      // Extract validation errors if available
      const validationErrorsArray = error.response?.data?.errors || [];
      if (validationErrorsArray.length > 0) {
        // Convert array of validation errors to field-specific object
        const validationErrors: Record<string, string> = {};
        validationErrorsArray.forEach((err: any) => {
          if (err.field && err.message) {
            validationErrors[err.field] = err.message;
          }
        });
        setServerErrors(validationErrors);
      } else {
        setServerErrors({});
        toast({
          title: 'Error',
          description: error.response?.data?.error?.message || error.response?.data?.message || `Failed to ${editingCompany ? 'update' : 'create'} company`,
          variant: 'destructive',
        });
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-background">
      <div className="container mx-auto px-4 py-6 max-w-7xl">
        <MasterDataTable
          title="Company Management"
          description="Manage companies, legal information, and configurations"
          data={companies}
          columns={columns}
          isLoading={isLoading}
          onAdd={handleAdd}
          onEdit={handleEdit}
          onDelete={handleDelete}
          onRefresh={handleRefresh}
          onExport={handleExport}
          filters={filters}
          searchPlaceholder="Search companies by name, email, code, PAN, GST, or location..."
          allowBulkOperations={false}
          allowImportExport={true}
          // Server-side filtering and pagination props
          serverSide={true}
          pagination={pagination}
          onPageChange={setCurrentPage}
          onPageSizeChange={setPageSize}
          onSearchChange={setSearchTerm}
          onFiltersChange={setCurrentFilters}
          onSortChange={setSortConfig}
          currentSearch={searchTerm}
          currentFilters={currentFilters}
          currentSort={sortConfig}
        />

        <MasterDataForm
          title={editingCompany ? 'Edit Company' : 'Add New Company'}
          description={
            editingCompany
              ? 'Update company information, legal details, and configurations.'
              : 'Create a new company with all required legal information and settings.'
          }
          sections={formSections}
          isOpen={isFormOpen}
          onClose={() => {
            setIsFormOpen(false);
            setEditingCompany(null);
            setServerErrors({});
          }}
          onSubmit={handleFormSubmit}
          isLoading={isSubmitting}
          initialData={editingCompany}
          mode={editingCompany ? 'edit' : 'create'}
          serverErrors={serverErrors}
          entityName="Company"
        />
      </div>
    </div>
  );
};

export default CompanyManagement;